import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { cn } from "@/lib/cn";
import { buttonStyles, type ButtonSize, type ButtonVariant } from "./styles";
import { useMagnetic } from "@/motion/useMagnetic";
import { Spinner } from "./Spinner";

export interface GlowButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  /** Magnetic hover; on by default for `primary`. */
  magnetic?: boolean;
}

/**
 * Primary call-to-action button with backlit glow.
 * <GlowButton loading={isPending} leftIcon={<LogIn />}>Log in</GlowButton>
 */
export const GlowButton = forwardRef<HTMLButtonElement, GlowButtonProps>(function GlowButton(
  {
    variant = "primary",
    size = "md",
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth,
    magnetic,
    className,
    children,
    disabled,
    type = "button",
    ...rest
  },
  forwarded,
) {
  const magneticRef = useMagnetic<HTMLButtonElement>(0.2);
  const useMag = magnetic ?? variant === "primary";
  return (
    <button
      ref={(node) => {
        if (useMag) (magneticRef as { current: HTMLButtonElement | null }).current = node;
        if (typeof forwarded === "function") forwarded(node);
        else if (forwarded) forwarded.current = node;
      }}
      type={type}
      className={buttonStyles({ variant, size, fullWidth, className })}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner className="size-4" label="Please wait" /> : leftIcon}
      <span className={cn(loading && "opacity-90")}>{children}</span>
      {!loading && rightIcon}
    </button>
  );
});

/** Router link styled as a button. <ButtonLink to="/register" variant="ghost">Sign up</ButtonLink> */
export function ButtonLink({
  variant,
  size,
  fullWidth,
  className,
  ...props
}: LinkProps & { variant?: ButtonVariant; size?: ButtonSize; fullWidth?: boolean }) {
  return <Link className={buttonStyles({ variant, size, fullWidth, className })} {...props} />;
}
